
import { Maximize2, ShieldCheck, Settings, Gauge } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface CapabilityProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  percentage: number;
  afterPercentage?: number; // New prop for "after edgefi" percentage
}

const CapabilityCard = ({ icon, title, description, percentage, afterPercentage }: CapabilityProps) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow duration-300 h-full">
    <div className="flex items-start">
      <div className="mr-4 p-2 bg-purple-100 rounded-lg">
        {icon}
      </div>
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">{description}</p>
        <div className="mt-auto">
          {/* Before edgefi utilization */}
          <div className="flex justify-between mb-1">
            <span className="text-sm text-gray-600">Average Utilization Before edgefi</span>
            <span className="text-sm font-medium text-gray-500">{percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-3">
            <div 
              className="bg-gray-400 h-2 rounded-full" 
              style={{ width: `${percentage}%` }} 
            />
          </div>
          
          {/* After edgefi utilization - shown only if afterPercentage is provided */}
          {afterPercentage && (
            <>
              <div className="flex justify-between mb-1">
                <span className="text-sm text-gray-600">Average Utilization After edgefi</span>
                <span className="text-sm font-medium text-green-600">{afterPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-green-500 h-2 rounded-full" 
                  style={{ width: `${afterPercentage}%` }} 
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  </div>
);

interface HiddenCapabilitiesSectionProps {
  platform: string;
  capabilities: Omit<CapabilityProps, 'icon'>[];
}

export const HiddenCapabilitiesSection = ({ platform, capabilities }: HiddenCapabilitiesSectionProps) => {
  // Map of icons to use based on capability titles or other criteria
  const getIconForCapability = (index: number) => {
    const icons = [
      <ShieldCheck className="h-5 w-5 text-edgefi-purple" />,
      <Maximize2 className="h-5 w-5 text-edgefi-purple" />,
      <Settings className="h-5 w-5 text-edgefi-purple" />,
      <Gauge className="h-5 w-5 text-edgefi-purple" />
    ];
    return icons[index % icons.length];
  };

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-5">
            Untapped Potential
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
            {platform} Capabilities You Already Own
          </h2>
          <p className="text-xl text-gray-600">
            Most organizations use less than 40% of their existing security capabilities. 
            We help you unlock the full potential of your technology investments.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {capabilities.map((capability, index) => (
            <CapabilityCard
              key={index}
              icon={getIconForCapability(index)}
              title={capability.title}
              description={capability.description}
              percentage={capability.percentage}
              afterPercentage={capability.afterPercentage}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
